import React from 'react';
import { 
  Box, 
  TextField,
  Stack,
  Typography,
  Divider
} from '@mui/material';
import { LetterDraftView } from '../../../services/AppealsService';

interface LetterEditorProps {
  letterDraft: LetterDraftView;
  onChange: (updates: Partial<LetterDraftView>) => void;
}

const LetterEditor: React.FC<LetterEditorProps> = ({ letterDraft, onChange }) => {
  return (
    <Box>
      <Stack spacing={2}>
        {/* Subject */}
        <TextField
          fullWidth
          label="Subject Line"
          value={letterDraft.subject || ''}
          onChange={(e) => onChange({ subject: e.target.value })}
          placeholder="Re: Appeal for Claim #..."
        />

        {/* Salutation */}
        <TextField
          fullWidth
          label="Salutation"
          value={letterDraft.salutation || ''}
          onChange={(e) => onChange({ salutation: e.target.value })}
          placeholder="Dear Claims Review Department,"
        />

        <Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
            Opening
          </Typography>
        </Box>

        {/* Opening Paragraph */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Opening Paragraph"
          value={letterDraft.openingParagraph || ''}
          onChange={(e) => onChange({ openingParagraph: e.target.value })}
          placeholder="State the purpose of your appeal..."
          helperText="Introduce the appeal and state your intent"
        />

        <Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
            Body
          </Typography>
        </Box>

        {/* Main Body */}
        <TextField
          fullWidth
          multiline
          rows={6}
          label="Main Body"
          value={letterDraft.body || ''}
          onChange={(e) => onChange({ body: e.target.value })}
          placeholder="Detailed explanation of your appeal..."
          helperText="Provide detailed facts and circumstances"
        />

        {/* Medical Necessity Argument */}
        {letterDraft.medicalNecessityArgument !== undefined && (
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Medical Necessity Argument"
            value={letterDraft.medicalNecessityArgument || ''}
            onChange={(e) => onChange({ medicalNecessityArgument: e.target.value })}
            placeholder="Explain why the service was medically necessary..."
            helperText="Clinical justification for the service"
          />
        )}

        {/* Policy Compliance Argument */}
        {letterDraft.policyComplianceArgument !== undefined && (
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Policy Compliance Argument"
            value={letterDraft.policyComplianceArgument || ''}
            onChange={(e) => onChange({ policyComplianceArgument: e.target.value })}
            placeholder="Reference applicable policy provisions..."
            helperText="Cite relevant payer policies and guidelines"
          />
        )}

        <Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
            Supporting Information
          </Typography>
        </Box>

        {/* Supporting Documentation */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Supporting Documentation"
          value={letterDraft.supportingDocumentation || ''}
          onChange={(e) => onChange({ supportingDocumentation: e.target.value })}
          placeholder="List enclosed supporting documents..."
          helperText="Reference any attachments or supporting materials"
        />

        <Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
            Closing
          </Typography>
        </Box>

        {/* Closing Paragraph */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Closing Paragraph"
          value={letterDraft.closingParagraph || ''}
          onChange={(e) => onChange({ closingParagraph: e.target.value })}
          placeholder="Summarize your request..."
        />

        {/* Requested Action */}
        <TextField
          fullWidth
          label="Requested Action"
          value={letterDraft.requestedAction || ''}
          onChange={(e) => onChange({ requestedAction: e.target.value })}
          placeholder="We respectfully request reconsideration and payment of..."
          helperText="Clearly state what action you are requesting"
        />

        {/* Signature */}
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Signature Block"
          value={letterDraft.signature || ''}
          onChange={(e) => onChange({ signature: e.target.value })}
          placeholder="Sincerely,&#10;[Name]&#10;[Title]"
        />

        {/* AI Rationale (Read-only) */}
        {letterDraft.aiGeneratedRationale && (
          <Box sx={{ 
            backgroundColor: '#f0f9ff', 
            p: 2, 
            borderRadius: 1,
            border: '1px solid #bae6fd'
          }}>
            <Typography variant="subtitle2" sx={{ color: '#0284c7', mb: 1 }}>
              AI Generation Notes
            </Typography>
            <Typography variant="body2" sx={{ color: '#0369a1', whiteSpace: 'pre-wrap' }}>
              {letterDraft.aiGeneratedRationale}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default LetterEditor;
